<script setup lang="ts">
import {computed} from 'vue';
import SvgIllustration from '@/vue/atoms/svg-illustration.vue';
import {SHOW_PAYMENT_FEATURES, IS_DEBUG} from '@/ts/utils/consts';
import {AccountRouteName} from '@/ts/router/pages-path';
import {useAccountRoute} from '@/ts/composables/stateful/use-account-route';

const {toAccount, currentName} = useAccountRoute();

const activeTab = computed(() => currentName.value ?? AccountRouteName.profile);
</script>
<template>
  <div class="page">
    <div class="header-row">
      <div class="page-title">
        <span class="title-label">{{ $t('account') }}</span>
        <svg-illustration name="right-down-arrow.svg" inner-class="inner-right"/>
      </div>
      <div class="tabs-wrapper">
        <q-tabs :model-value="activeTab" class="tabs" no-caps>
          <q-route-tab :name="AccountRouteName.profile" :to="toAccount(AccountRouteName.profile)" :label="$t('profile')"/>
          <q-route-tab
            v-if="SHOW_PAYMENT_FEATURES"
            :name="AccountRouteName.subscription"
            :to="toAccount(AccountRouteName.subscription)"
            :label="$t('current_plan')"
          />
          <q-route-tab :name="AccountRouteName.security" :to="toAccount(AccountRouteName.security)" :label="$t('security')"/>
          <q-route-tab :name="AccountRouteName.preferences" :to="toAccount(AccountRouteName.preferences)" :label="$t('preferences')"/>
          <q-route-tab :name="AccountRouteName.badges" :to="toAccount(AccountRouteName.badges)" :label="$t('badges')"/>
          <!-- eslint-disable-next-line @intlify/vue-i18n/no-raw-text -->
          <q-route-tab v-if="IS_DEBUG" :name="AccountRouteName.debug" :to="toAccount(AccountRouteName.debug)" label="Debug"/>
        </q-tabs>
      </div>
    </div>

    <div class="main">
      <router-view/>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.tabs {
  @include tabs;
  min-width: max-content;

  :deep(.q-tabs__content) {
    flex-wrap: nowrap;
  }

  :deep(.q-tabs__arrow) {
    display: none;
  }
}

.page {
  @include page-alignment;
  padding-top: 0;
  overflow-x: hidden;
}

.header-row {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: var(--Spacing-spacing-4);
  position: sticky;
  top: 0;
  z-index: 1;
  background-color: var(--Neutral-UI-neutral-ui-10);
  padding-top: var(--Spacing-spacing-4);
  padding-bottom: var(--Spacing-spacing-4);

  @include screen-md {
    padding-top: var(--Spacing-spacing-3);
    flex-direction: row;
    align-items: center;
    gap: var(--Spacing-spacing-6);
  }
}

.tabs-wrapper {
  overflow-x: auto;
  scrollbar-width: none;
  width: calc(100% + 2 * var(--Spacing-spacing-5));
  margin-left: calc(-1 * var(--Spacing-spacing-5));
  padding-left: var(--Spacing-spacing-5);
  padding-right: var(--Spacing-spacing-5);

  &::-webkit-scrollbar {
    display: none;
  }

  @include screen-md {
    width: 100%;
    margin-left: 0;
    padding-left: 0;
    padding-right: 0;
  }
}

.page-title {
  display: flex;
  align-items: center;
  gap: var(--Spacing-spacing-3);
  flex-shrink: 0;
}

.title-label {
  @include font-h1-bold;
  color: var(--Neutral-UI-neutral-ui-100);
}

.main {
  overflow-x: hidden;
}
</style>
